import { Card, Form, Image, Input, Typography } from 'antd';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../../shared/components/modal/modal';
import PageSpinner from '../../../../shared/components/page-spinner/page-spinner';
import { useCheckTotpRequirements } from '../../../../shared/utils/check-totp-requirements';
import { handleServerFormErrors } from '../../../../shared/utils/error-handling/error-handling';
import { useAppDispatch } from '../../../../store/hooks';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useEnableUserTotpMutation, useGetUserTotpQuery } from '../../api/user-api';
import { currentUserAlertId, totpModalEnableId } from '../../constants/constants';
import { UserTotpCode } from '../../models/user-totp-code';
import styles from './enable-totp-modal.module.css';

const EnableTotp = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t } = useTranslation('current-user');
  const { data: totpData } = useGetUserTotpQuery();
  const [editTotp, { isLoading }] = useEnableUserTotpMutation();
  const dispatch = useAppDispatch();
  const isTotpRequiredButNotEnabled = useCheckTotpRequirements();

  const closeTotpModal = () => dispatch(closeModal(totpModalEnableId));

  const [form] = Form.useForm<UserTotpCode>();

  const initialValues: UserTotpCode = useMemo(() => {
    return {
      authCode: '',
      secret: totpData?.secret || '',
    };
  }, [totpData]);

  useEffect(() => {
    form.resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const submitForm = (formData: UserTotpCode) => {
    editTotp({
      ...formData,
      secret: totpData?.secret || '',
    })
      .unwrap()
      .then(() => {
        closeTotpModal();
        dispatch(
          setAlert({
            id: currentUserAlertId,
            type: 'success',
            title: t('totp.message.enabled'),
          }),
        );
      })
      .catch(error => {
        handleServerFormErrors(form, error.data?.errors || { authCode: [t('totp.message.error')] });
      });
  };

  return (
    <Modal
      title={t('totp.totpLabel')}
      isOpen={isOpen}
      isLoading={isLoading}
      afterClose={afterClose}
      onOk={() => form.submit()}
      onCancel={() => !isLoading && !isTotpRequiredButNotEnabled && closeTotpModal()}
      modalProps={{
        closable: !isTotpRequiredButNotEnabled && !isLoading,
        keyboard: !isTotpRequiredButNotEnabled,
      }}
    >
      <Card>
        <Typography>{t('totp.secret')}</Typography>
        <Typography>{totpData?.secret}</Typography>
        <div className={styles.qrCode}>
          {!totpData?.qrCodeImage ? (
            <PageSpinner />
          ) : (
            <Image height={300} preview={false} src={`data:image/png;base64,${totpData?.qrCodeImage}`} />
          )}
        </div>
        <Form form={form} onFinish={submitForm}>
          <Typography>{t('totp.authSecret')}</Typography>
          <Form.Item
            name="authCode"
            rules={[
              {
                required: true,
                message: t('totp.message.emptySecret'),
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Card>
    </Modal>
  );
};

const EnableTotpModal = (): JSX.Element => {
  return <LazyModal modalId={totpModalEnableId} Component={EnableTotp} />;
};
export default EnableTotpModal;
